import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export const ParticleOrb = ({ converge = true, size = 220 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const W = c.width = size * 2, H = c.height = size * 2, cx = W / 2, cy = H / 2;
    const pts = Array.from({ length: 140 }, () => {
      const a = Math.random() * Math.PI * 2, r = size * (0.9 + Math.random() * 0.9);
      return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, a, r: 0, tr: size * (0.25 + Math.random() * 0.3), sp: 0.004 + Math.random() * 0.006, s: 1 + Math.random() * 2 };
    });
    let raf, t = 0;
    const draw = () => {
      t += 1; ctx.clearRect(0, 0, W, H);
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.5);
      g.addColorStop(0, 'rgba(163,230,53,0.35)'); g.addColorStop(1, 'rgba(6,182,212,0)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, size * 0.5, 0, Math.PI * 2); ctx.fill();
      pts.forEach(p => {
        if (converge) { p.a += p.sp; const tx = cx + Math.cos(p.a) * p.tr, ty = cy + Math.sin(p.a + t * 0.002) * p.tr; p.x += (tx - p.x) * 0.03; p.y += (ty - p.y) * 0.03; }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${80 + Math.sin(t * 0.01 + p.a) * 40},100%,65%,0.9)`; ctx.shadowBlur = 12; ctx.shadowColor = 'rgba(163,230,53,0.8)'; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [converge, size]);
  return <canvas ref={ref} style={{ width: size, height: size }} className="mx-auto" data-testid="awakening-orb" />;
};

export const TypeText = ({ text, speed = 28, onDone, className = '' }) => {
  const [shown, setShown] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => { i += 1; setShown(text.slice(0, i)); if (i >= text.length) { clearInterval(id); onDone?.(); } }, speed);
    return () => clearInterval(id);
  }, [text, speed]);
  const visible = shown.startsWith(text.slice(0, Math.min(shown.length, text.length))) ? shown : '';
  return <p className={className}>{visible}<span className="inline-block w-[2px] h-[1em] bg-lime-400 ml-1 align-middle animate-pulse" /></p>;
};

export const CountUp = ({ value = 0, duration = 1.2, suffix = '' }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const step = (t) => { const k = Math.min(1, (t - t0) / (duration * 1000)); setV(Math.round(value * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{v}{suffix}</span>;
};

export const HudFrame = ({ children, className = '' }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={`relative rounded-2xl border border-lime-400/20 bg-black/50 backdrop-blur-md p-5 ${className}`}>
    {['top-0 left-0 border-t border-l', 'top-0 right-0 border-t border-r', 'bottom-0 left-0 border-b border-l', 'bottom-0 right-0 border-b border-r'].map(c => (
      <span key={c} className={`absolute w-4 h-4 ${c} border-lime-400 rounded-sm`} />
    ))}
    {children}
  </motion.div>
);

export const Dial = ({ label, value, min, max, step = 1, unit, onChange, testId }) => (
  <div className="space-y-2">
    <div className="flex items-baseline justify-between">
      <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</span>
      <span className="text-2xl font-black text-lime-300 tabular-nums" data-testid={`${testId}-value`}>{value}<span className="text-xs text-zinc-500 ml-1">{unit}</span></span>
    </div>
    <input data-testid={testId} type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))}
      className="w-full h-1.5 appearance-none rounded-full bg-zinc-800 accent-lime-400 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-lime-400 [&::-webkit-slider-thumb]:shadow-[0_0_18px_rgba(163,230,53,0.8)]" />
  </div>
);

export const Chips = ({ label, options, value, onChange, testId }) => (
  <div className="space-y-2">
    <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o.v} type="button" data-testid={`${testId}-${o.v}`} onClick={() => onChange(o.v)}
          className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all ${value === o.v ? 'bg-lime-400 text-black border-lime-400 shadow-[0_0_18px_rgba(163,230,53,0.5)]' : 'border-zinc-700 text-zinc-300 hover:border-lime-400/50'}`}>
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
